import { Stack, TextField, Typography } from "@material-ui/core";
import type { WhatsappTemplateParam } from "../../../../@types/sharedSchema";
import { paramSourceLabel } from "./templateHelpers";
import { emptyListingFieldNote } from "./templateListing";

type Props = {
  /** The template's listing FIELDS (`isListingFieldBinding`) — never its link, which is per recipient. */
  params: readonly WhatsappTemplateParam[];
  values: Record<string, string>;
  onChange: (paramName: string, value: string) => void;
  /** A listing was picked and filled these; otherwise the realtor types them. */
  listingPicked: boolean;
  disabled?: boolean;
};

/**
 * The listing's own fields in a template send: the same for every recipient, ALWAYS shown and
 * editable. Picking a listing fills them; without one the realtor types them — a template that only
 * reads a listing's city must never make them pick a listing first (Haim, 2026-10-25). An empty one
 * goes out with the template's fallback when it has one, and is marked as missing when it does not.
 * One component for every dialog that sends a template.
 */
export default function TemplateListingFields({ params, values, onChange, listingPicked, disabled = false }: Props) {
  if (params.length === 0) return null;
  return (
    <Stack spacing={1.5}>
      <Typography variant="caption" sx={{ color: "text.secondary", textAlign: "start" }}>
        {listingPicked ? "מהנכס שבחרתם (אפשר לערוך):" : "פרטי הנכס, זהים לכל הנמענים:"}
      </Typography>
      {params.map((param) => {
        const value = values[param.paramName] ?? "";
        const empty = !value.trim();
        return (
          <TextField
            key={param.paramName}
            fullWidth
            size="small"
            disabled={disabled}
            label={param.hebrewLabel?.trim() || paramSourceLabel(param.binding)}
            placeholder={param.exampleValue}
            value={value}
            multiline={param.binding === "listing.description"}
            maxRows={4}
            onChange={(event) => onChange(param.paramName, event.target.value)}
            helperText={empty ? emptyListingFieldNote(param, { listingPicked }) : undefined}
            // Red only once a listing was picked and left it empty: before that, an empty field is
            // simply not written yet, and the send button already waits for it.
            error={listingPicked && empty && !param.fallbackValue?.trim()}
            inputProps={param.maxChars ? { maxLength: param.maxChars } : undefined}
          />
        );
      })}
    </Stack>
  );
}
